import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Card as ReactstrapCard,
    CardBody,
    CardHeader,
    Col,
    Row,
    // TabContent,
    TabPane,
} from 'reactstrap';
import classnames from 'classnames';
import dayjs from "dayjs";
import { ConfirmOrCancelModal } from "components/common/standardComponents";
import PatientAppointmentHistory from "./patientAppointmentHistory";
import PatientUpcomingAppointment from "./patientUpcomingAppointments"
import PatientCareOrderHistory from "./patientCareOrderHistory";
import { patientDetails as routeToPatientDetails } from '../../../routes';
import PatientDetailsSummary from "./patientDetailsSummary";
import PatientInfoNav from "./patientInfoNav";
import LoadingIndicator from "components/loadingIndicator";
import useLoadPatientAppointmentHistory from "../hooks/useLoadPatientAppointmentHistory";
import useLoadPatientCareOrderHistory from "../hooks/useLoadPatientCareOrderHistory";
import useLoadPatientOpenWorkflows from "../hooks/useLoadPatientOpenWorkflows";
import useLoadPatientCoverage from "../hooks/useLoadPatientCoverage";
import PatientOpenworkflows from "./patientOpenWorkflows";
import PatientCareOrderDetails from "./patientCareOrderDetails"
import { genderCodeToDisplayName } from '../../../lib/standardLibrary';
import PatientCoverage from "./patientCoverage";
import '../css/patientInfoTabbedCollapsable.css';
import { ActiveTabProvider, useActiveTab } from "./activeTabContext";
import { PatientInfoTabName, TabContent } from "../patientDetailsView";

export default function PatientInfoTabbedCollapsable() {
    const dispatch = useDispatch();

    const [showConfirmMorePatientInfoModal, setShowConfirmMorePatientInfoModal] = useState(false);

    const patient = useSelector((state: any) => state.activePatient);
    const patientDetails = patient.details;
    const patientConfig = useSelector((state: any) => state.config.patient);
    const schedulingConfig = useSelector((state: any) => state.config.scheduling);
    const routePrefix = useSelector((state: any) => state.config.instance.routePrefix);

    const appointmentHistoryRecords = useSelector((state: any) => state.activePatient.history);
    const [isPatientAppointmentHistoryLoading, setIsPatientAppointmentHistoryLoading] = useState(false);
    const shouldLoadPatientAppointmentHistory = patientConfig.isPatientHistoryEnabled
        && patientConfig.showPatientHistoryInPatientInfoPanel
        && !appointmentHistoryRecords
        && !isPatientAppointmentHistoryLoading;

    useLoadPatientAppointmentHistory(
        patient.id,
        shouldLoadPatientAppointmentHistory,
        setIsPatientAppointmentHistoryLoading
    );

    const careOrders = useSelector((state: any) => state.activePatient.careOrderHistory);
    const [isPatientCareOrderHistoryLoading, setIsPatientCareOrderHistoryLoading] = useState(false);
    const shouldLoadPatientCareOrderHistory = patientConfig.isPatientCareOrderHistoryEnabled
        && !careOrders
        && !isPatientCareOrderHistoryLoading;

    useLoadPatientCareOrderHistory(
        patient.id,
        shouldLoadPatientCareOrderHistory,
        setIsPatientCareOrderHistoryLoading
    );

    const openWorkflows = useSelector((state: any) => state.activePatient.openWorkflows);
    const isPatientOpenWorkflowsLoading = useSelector((state: any) => state.activePatient.isLoadingOpenWorkflows);
    const shouldLoadPatientOpenWorkflows = patientConfig.isDecisionSupportWorkflowHistoryEnabled
        && !openWorkflows
        && !isPatientOpenWorkflowsLoading;

    useLoadPatientOpenWorkflows(
        patient.id,
        shouldLoadPatientOpenWorkflows,
        schedulingConfig.bookOpenDecisionSupportWorkflowMaxAgeInDays,
    );

    const patientCoverageDetails = useSelector((state: any) => state.activePatient.patientCoverage);
    const [isPatientCoverageDetailsLoading, setIsPatientCoverageDetailsLoading] = useState(false);
    const shouldLoadPatientCoverage = patientConfig.enablePatientAccessCuratorInsuranceCoverage
        && patientConfig.enablePatientAccessCurator
        && !patientCoverageDetails
        && !isPatientCoverageDetailsLoading;

    useLoadPatientCoverage(
        patient.details,
        patientConfig.patientAccessCuratorDataValidityInDays,
        patientConfig.enablePatientAccessCuratorCoverageAutoFetch,
        shouldLoadPatientCoverage,
        setIsPatientCoverageDetailsLoading,
    );

    const activeCareOrderDetails = useSelector((state: any) => state.careOrder.activeCareOrderDetails);

    const isPatientHeaderAutoExpanded = schedulingConfig.autoExpandPatientHeaderForVisibleNotes;
    const hasCareOrder = useSelector((state: any) => !!state.careOrder.careOrderVisitIdentifier || !!state.careOrder.externalReferralOrderId);
    const enableOrderDetailsPanel = useSelector((state: any) => state.config.orderScheduling.enableOrderDetailsPanel);
    const isOrderDetailsHeaderAutoExpanded = useSelector((state: any) => state.config.orderScheduling.autoExpandOrderDetailsPanel);

    let initialTab;
    if (hasCareOrder && enableOrderDetailsPanel && (isOrderDetailsHeaderAutoExpanded || isPatientHeaderAutoExpanded)) {
        initialTab = PatientInfoTabName.patientCareOrderDetails;
    }
    else if (isPatientHeaderAutoExpanded) {
        initialTab = PatientInfoTabName.patientDetails
    }
    else {
        initialTab = PatientInfoTabName.empty;
    }

    return (
        <ActiveTabProvider initialTab={initialTab}>
            <Card>
                <PatientInfoHeader patientDetails={patientDetails} />
                <CardBody className="PatientInfoCardBody">
                    <PatientInfoNav
                        activePatientNotes={patientDetails?.notes}
                        activeCareOrderNotes={activeCareOrderDetails?.notes}
                    />
                    <TabContent>
                        {hasCareOrder && enableOrderDetailsPanel && (
                            <TabPane tabId={PatientInfoTabName.patientCareOrderDetails}>
                                <PatientCareOrderDetails />
                            </TabPane>
                        )}
                        <TabPane tabId={PatientInfoTabName.patientDetails}>
                            <PatientDetailsSummary
                                patientDetails={patientDetails}
                                patientConfig={patientConfig}
                                setShowConfirmMorePatientInfoModal={setShowConfirmMorePatientInfoModal}
                            />
                        </TabPane>
                        <TabPane tabId={PatientInfoTabName.coverage}>
                            <PatientCoverage />
                        </TabPane>
                        {patientConfig.isPatientHistoryEnabled && patientConfig.showPatientHistoryInPatientInfoPanel &&
                            <>
                                <TabPane tabId={PatientInfoTabName.upcomingAppointment}>
                                    <Row style={{ marginTop: '10px' }}>
                                        <Col>
                                            {isPatientAppointmentHistoryLoading &&
                                                <div>
                                                    <LoadingIndicator />
                                                </div>
                                            }
                                            {!isPatientAppointmentHistoryLoading &&
                                                <PatientUpcomingAppointment
                                                    showLoseProgressWarning={true}
                                                    enableShowPSAandMHDfilters={false}
                                                />
                                            }
                                        </Col>
                                    </Row>
                                </TabPane>
                                <TabPane tabId={PatientInfoTabName.appointmentHistory}>
                                    <Row style={{ marginTop: '10px' }}>
                                        <Col>
                                            {isPatientAppointmentHistoryLoading &&
                                                <div>
                                                    <LoadingIndicator />
                                                </div>
                                            }
                                            {!isPatientAppointmentHistoryLoading &&
                                                <PatientAppointmentHistory
                                                    showLoseProgressWarning={true}
                                                    enableShowPSAandMHDfilters={false}
                                                />
                                            }
                                        </Col>
                                    </Row>
                                </TabPane>
                            </>
                        }
                        {patientConfig.isPatientCareOrderHistoryEnabled &&
                            <TabPane tabId={PatientInfoTabName.careOrderHistory}>
                                <Row style={{ marginTop: '10px' }}>
                                    <Col>
                                        {isPatientCareOrderHistoryLoading &&
                                            <div>
                                                <LoadingIndicator />
                                            </div>
                                        }
                                        {!isPatientCareOrderHistoryLoading &&
                                            <PatientCareOrderHistory
                                                showBookButton={false}
                                                showCloseButton={false}
                                                showFilters={false}
                                                setIsPatientCareOrderHistoryLoading={setIsPatientCareOrderHistoryLoading}
                                            />
                                        }
                                    </Col>
                                </Row>
                            </TabPane>
                        }
                        {patientConfig.isDecisionSupportWorkflowHistoryEnabled &&
                            <TabPane tabId={PatientInfoTabName.decisionSupportWorkflowHistory}>
                                <Row style={{ marginTop: '10px' }}>
                                    <Col>
                                        {isPatientOpenWorkflowsLoading &&
                                            <div>
                                                <LoadingIndicator />
                                            </div>
                                        }
                                        {!isPatientOpenWorkflowsLoading &&
                                            <PatientOpenworkflows
                                                showLoseProgressWarning={true}
                                                showBookButton={schedulingConfig.enableBookOpenDecisionSupportWorkflow}
                                            />
                                        }
                                    </Col>
                                </Row>
                            </TabPane>
                        }
                    </TabContent>
                    <ConfirmOrCancelModal
                        className={"confirmMoreInfoModal"}
                        title={"Confirm"}
                        body={"If you continue you will lose your current progress. Are you sure you want to leave?"}
                        isOpen={showConfirmMorePatientInfoModal}
                        confirmLabel={"Confirm"}
                        cancelLabel={"Cancel"}
                        onConfirm={() => {
                            setShowConfirmMorePatientInfoModal(false);
                            dispatch(routeToPatientDetails(routePrefix, patientDetails?.referenceId));
                        }}
                        onCancel={() => setShowConfirmMorePatientInfoModal(false)}
                    />
                </CardBody>
            </Card>
        </ActiveTabProvider>
    );
}

const PatientInfoHeader = ({ patientDetails }: { patientDetails: any }) => (
    <CardHeader className="PatientInfoCardHeader">
        <Row style={{ alignItems: "baseline" }}>
            <Col lg="auto" style={{ paddingLeft: "0" }}>
                <h4>
                    {`${patientDetails?.firstName} ${patientDetails?.lastName}`}
                </h4>
            </Col>
            <Col lg="auto" style={{ padding: "0" }}>
                <h6>
                    {patientDetails?.dateOfBirth ? dayjs(patientDetails?.dateOfBirth).format('MM/DD/YYYY') : '-'}
                </h6>
            </Col>
            <Col lg="auto" style={{ padding: "0px 3px" }}>
                <h5>&#8226;</h5>
            </Col>
            <Col lg="auto" style={{ padding: "0" }}>
                <h6>
                    {genderCodeToDisplayName(patientDetails?.gender)}
                </h6>
            </Col>
        </Row>

    </CardHeader>
);

type CardProps = React.ComponentProps<typeof ReactstrapCard>

const Card: React.FC<CardProps> = (props) => {
    const { activeTab } = useActiveTab();
    return <ReactstrapCard className={classnames({ PatientInfoCard: !activeTab.name, PatientInfoCardActive: activeTab.name })} {...props} />
}